import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const BusinessSubmissionsContext = createContext();

export const useBusinessSubmissions = () => {
  const context = useContext(BusinessSubmissionsContext);
  if (!context) {
    throw new Error('useBusinessSubmissions must be used within a BusinessSubmissionsProvider');
  }
  return context;
};

export const BusinessSubmissionsProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // FIX: Destructure 'adminId' directly from useAuth. 
  // Your AuthProvider ensures this has a value (from state or localStorage).
  const { makeAuthenticatedRequest, loginType, user, adminId } = useAuth();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://admin-golang-backend-8cdf9b481f22.herokuapp.com';
  
  // SIMPLIFIED: Always use the admin path
  const getSubmissionsBaseUrl = useCallback(() => {
    return `${API_BASE_URL}/admin/business-submissions/api/v1/business-submissions`;
  }, [API_BASE_URL]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    return data;
  };

  // FIX: Updated to use the adminId variable from useAuth
  const getCurrentAdminId = useCallback(() => {
    // 1. Try the direct adminId from AuthContext (most reliable)
    if (adminId) return adminId;
    
    // 2. Fallbacks for safety (CamelCase vs snake_case)
    return user?.adminId || user?.admin_id || user?.id;
  }, [adminId, user]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate Access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin') {
      throw new Error('Only admin users can access business submissions');
    }
  }, [loginType]);

  // Get all suggestions
  const getSuggestions = useCallback(async (options = {}) => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);
      if (options.status) queryParams.append('status', options.status);
      if (options.category) queryParams.append('category', options.category);
      if (options.city) queryParams.append('city', options.city);
      if (options.dateFrom) queryParams.append('date_from', options.dateFrom);
      if (options.dateTo) queryParams.append('date_to', options.dateTo);
      if (options.search) queryParams.append('search', options.search);

      const baseUrl = getSubmissionsBaseUrl();
      const fullUrl = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl;
      
      const response = await makeAuthenticatedRequest(fullUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch business suggestions';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Get suggestion by ID
  const getSuggestionById = useCallback(async (suggestionId) => {
    validateAccess();
    if (!suggestionId) throw new Error('Suggestion ID is required');

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/suggestion/${encodeURIComponent(suggestionId)}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get suggestion by ID';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Get statistics
  const getStats = useCallback(async () => {
    validateAccess();
    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/stats`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get statistics';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl]);

  // Approve Suggestion
  const approveSuggestion = useCallback(async (suggestionId, notes = null) => {
    validateAccess();
    if (!suggestionId) throw new Error('Suggestion ID is required');

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const currentAdminId = getCurrentAdminId();
      
      // FIX: Better error message and check
      if (!currentAdminId) {
        throw new Error("Admin ID not found. Please refresh the page or log in again.");
      }
      
      const body = {
        admin_id: currentAdminId,
        notes: notes
      };

      const response = await makeAuthenticatedRequest(
        `${baseUrl}/suggestion/${encodeURIComponent(suggestionId)}/approve`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to approve suggestion';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl, getCurrentAdminId]);

  // Reject Suggestion
  const rejectSuggestion = useCallback(async (suggestionId, notes = null) => {
    validateAccess();
    if (!suggestionId) throw new Error('Suggestion ID is required');

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const currentAdminId = getCurrentAdminId();

      if (!currentAdminId) {
        throw new Error("Admin ID not found. Please refresh the page or log in again.");
      }
      
      const body = {
        admin_id: currentAdminId,
        notes: notes
      };
      
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/suggestion/${encodeURIComponent(suggestionId)}/reject`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to reject suggestion';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl, getCurrentAdminId]);

  // Mark Suggestion as Duplicate
  const markSuggestionAsDuplicate = useCallback(async (suggestionId, notes = null) => {
    validateAccess();
    if (!suggestionId) throw new Error('Suggestion ID is required');

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getSubmissionsBaseUrl();
      const currentAdminId = getCurrentAdminId();
      
      if (!currentAdminId) {
        throw new Error("Admin ID not found. Please refresh the page or log in again.");
      }

      const body = {
        admin_id: currentAdminId,
        notes: notes
      };
      
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/suggestion/${encodeURIComponent(suggestionId)}/duplicate`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to mark suggestion as duplicate';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getSubmissionsBaseUrl, getCurrentAdminId]);

  // --- Convenience Methods ---

  const getPendingSuggestions = useCallback(async (options = {}) => {
    return await getSuggestions({ ...options, status: 'pending' });
  }, [getSuggestions]);

  const getApprovedSuggestions = useCallback(async (options = {}) => {
    return await getSuggestions({ ...options, status: 'approved' });
  }, [getSuggestions]);

  const getRejectedSuggestions = useCallback(async (options = {}) => {
    return await getSuggestions({ ...options, status: 'rejected' });
  }, [getSuggestions]);

  const getDuplicateSuggestions = useCallback(async (options = {}) => {
    return await getSuggestions({ ...options, status: 'duplicate' });
  }, [getSuggestions]);

  const searchSuggestions = useCallback(async (term, options = {}) => {
    return await getSuggestions({ ...options, search: term });
  }, [getSuggestions]);

  // Format Data for Table/UI
  const formatSuggestionData = useCallback((suggestion) => {
    const getStatusColor = (status) => {
      const s = (status || '').toLowerCase();
      const colors = {
        'pending': 'orange',
        'approved': 'green',
        'rejected': 'red',
        'duplicate': 'gray',
      };
      return colors[s] || 'blue';
    };

    const suggestedBy = suggestion.first_name || suggestion.last_name 
      ? `${suggestion.first_name || ''} ${suggestion.last_name || ''}`.trim() 
      : 'Anonymous User';

    return {
      ...suggestion,
      statusColor: getStatusColor(suggestion.submission_status),
      suggestedBy: suggestedBy,
      formattedDate: suggestion.suggestion_date 
        ? new Date(suggestion.suggestion_date).toLocaleDateString() 
        : 'N/A',
      hasEmail: !!suggestion.business_email,
      hasPhone: !!suggestion.business_phone,
      hasImage: !!suggestion.business_image_url,
      hasCoordinates: !!(suggestion.business_coordinate_lat && suggestion.business_coordinate_lng),
    };
  }, []);

  // Export Data Logic
  const exportSuggestionsData = useCallback(async (filters = {}) => {
    validateAccess();

    const exportOptions = {
      ...filters,
      limit: 10000,
      offset: 0,
    };

    const result = await getSuggestions(exportOptions);
    
    const exportData = result.suggestions.map(s => ({
      'Suggestion ID': s.suggestion_id,
      'Business Name': s.business_name,
      'Category': s.business_category || '',
      'City': s.business_city || '',
      'Status': s.submission_status || '',
      'Date': s.suggestion_date ? new Date(s.suggestion_date).toLocaleDateString() : '',
      'Suggested By First Name': s.first_name || '',
      'Suggested By Last Name': s.last_name || '',
    }));

    return {
      data: exportData,
      count: result.suggestions.length,
      filename: `business_suggestions_${new Date().toISOString().split('T')[0]}.csv`
    };
  }, [getSuggestions, validateAccess]);

  const value = {
    loading,
    error,
    getSuggestions,
    getSuggestionById,
    getStats,
    approveSuggestion,
    rejectSuggestion,
    markSuggestionAsDuplicate,
    getPendingSuggestions,
    getApprovedSuggestions,
    getRejectedSuggestions,
    getDuplicateSuggestions,
    searchSuggestions,
    exportSuggestionsData,
    formatSuggestionData,
    clearError,
    getEndpointInfo: () => ({
      baseUrl: getSubmissionsBaseUrl(),
      accessLevel: loginType,
    })
  };

  return (
    <BusinessSubmissionsContext.Provider value={value}>
      {children}
    </BusinessSubmissionsContext.Provider>
  );
};